import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckBox, Collapsible, DropButton, Pagination, Tip } from 'grommet';
import { Checkmark, CircleAlert, CircleInformation, Filter, SettingsOption } from 'grommet-icons';
import { ApiError, ModelAuditLogService, TListModelAuditLogsRequest } from '/src/api';
import {
  AddButton,
  AuditLogEventTypeIcon,
  AuditLogIcon,
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  EntityIcon,
  FiltersDropdown,
  Link,
  LoadingSpinner,
  Text,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { Client, ClientFile, Equipment, Facility, Model, ModelAuditLog, ModelName, User } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { AuditorType, ModelAuditLogEventType, ModelAuditLogState, SelectOptions } from '/src/lib/types';
import { camelToSnakeCase, getModelUrl } from '/src/utils';

type AuditLogQueryState = {
  page: number;
  action_taken_by: AuditorType | undefined;
  model_audit_log_state_id: number | undefined;
  model_name: ModelName | undefined;
  model_id: number | undefined;
  user_id: number | undefined;
};

const AUDIT_LOG_DEFAULTS: AuditLogQueryState = {
  page: 1,
  action_taken_by: AuditorType.ExternalUser,
  model_audit_log_state_id: ModelAuditLogState.Unresolved,
  model_name: undefined,
  model_id: undefined,
  user_id: undefined,
};

const AUDIT_LOG_CLEARED: AuditLogQueryState = {
  ...AUDIT_LOG_DEFAULTS,
};

export const ModelAuditLogList: React.FC<ModelAuditLogListProps> = (props) => {
  /* Props */
  const { title = 'Logs List', hideHeader, surfaceId, ...boxProps } = props;

  /* Context */
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const queryClient = useQueryClient();
  const tooltips = tooltipMessages();

  /* Restorable state */
  const restorableList = useRestorableListState<AuditLogQueryState>({
    surfaceId: surfaceId || 'audit-log-list',
    defaults: AUDIT_LOG_DEFAULTS,
    standalone: true,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /* Draft state */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftFilters, setDraftFilters] = useState<Record<string, any>>({
    action_taken_by: queryState.action_taken_by ?? '',
    model_audit_log_state_id: queryState.model_audit_log_state_id ?? 0,
    model_name: queryState.model_name ?? '',
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isFiltersMenuHovered, setIsFiltersMenuHovered] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Record<number, boolean>>({});
  const [allRowsSelected, setAllRowsSelected] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  /* Build the API request */
  const request: TListModelAuditLogsRequest = useMemo(
    () => ({
      page: queryState.page,
      limit: 20,
      action_taken_by: queryState.action_taken_by || undefined,
      model_audit_log_state_id: queryState.model_audit_log_state_id || undefined,
      model_name: queryState.model_name || undefined,
      model_id: queryState.model_id || undefined,
      user_id: queryState.user_id || undefined,
    }),
    [queryState]
  );

  /* React Query */
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['model-audit-logs', userStore.user?.id, request],
    queryFn: async () => {
      const response = await ModelAuditLogService.list(request);
      return {
        meta: response.meta,
        data: response.data.filter((log) => Object.values(ModelName).includes(log.model_name as ModelName)),
      };
    },
    enabled: !!userStore.user,
    placeholderData: (prev) => prev,
  });

  const modelAuditLogs = queryData?.data;
  const metadata = queryData?.meta;

  /* Show toast on query error */
  useEffect(() => {
    if (queryError) {
      globalStore.handleApiError(queryError as ApiError, toastMessages.listAuditLogs.error);
    }
  }, [queryError]);

  /* Computed */
  const _isLoading = queryIsLoading || isFetching;
  const isRefetching = isFetching && !queryIsLoading;
  const hasModelAuditLogs = !!modelAuditLogs?.length;
  const hasSelectedRows = !!Object.values(selectedRows).find((row) => !!row);
  const draftActionTakenBy = draftFilters.action_taken_by || AUDIT_LOG_DEFAULTS.action_taken_by;

  const statusFilterOptions = useMemo(() => {
    const options: SelectOptions<number> = [{ label: 'All', value: 0 }];

    if (draftActionTakenBy !== AuditorType.System && draftActionTakenBy !== AuditorType.InternalUser) {
      options.push({ label: 'Unresolved', value: 1 });
      options.push({ label: 'Resolved', value: 2 });
    }

    return options;
  }, [draftActionTakenBy]);

  /* Methods */
  const getModelLabel = (log: ModelAuditLog): string => {
    const isDelete = log.model_audit_log_event_type.name === ModelAuditLogEventType.Delete;
    const isCreate = log.model_audit_log_event_type.name === ModelAuditLogEventType.Create;

    let modelData: unknown = log.model;
    if (!modelData && isDelete && log.old_data) {
      modelData = log.old_data;
    } else if (!modelData && isCreate && log.new_data) {
      modelData = log.new_data;
    }

    if (!modelData) return '';

    let model: unknown = {};

    if ((isDelete || isCreate) && !log.model) {
      Object.keys(modelData).forEach((key) => {
        const val = (modelData as Record<string, unknown>)[key];
        (model as Record<string, unknown>)[camelToSnakeCase(key)] = val;
      });
    } else {
      model = modelData;
    }

    switch (log.model_name) {
      case ModelName.Client:
        return String((model as Client).name ?? '');
      case ModelName.ClientFile:
        return String((model as ClientFile).name ?? '');
      case ModelName.Facility:
        return String((model as Facility).address_line1 ?? '');
      case ModelName.Equipment:
        return `${(model as Equipment).manufacturer ?? ''}: ${(model as Equipment).serial_number ?? ''}`;
      case ModelName.EquipmentUsage:
        return 'Equipment Usage';
      case ModelName.User:
        return String((model as User).name ?? '');
      default:
        return '';
    }
  };

  const applyFilters = useCallback(() => {
    const committed: Partial<AuditLogQueryState> = {
      action_taken_by: draftFilters.action_taken_by || undefined,
      model_name: draftFilters.model_name || undefined,
      page: 1,
    };

    if (
      draftFilters.action_taken_by === AuditorType.System ||
      draftFilters.action_taken_by === AuditorType.InternalUser
    ) {
      committed.model_audit_log_state_id = undefined;
      setDraftFilters((prev) => ({ ...prev, model_audit_log_state_id: 0 }));
    } else {
      committed.model_audit_log_state_id = draftFilters.model_audit_log_state_id || undefined;
    }

    setQueryState(committed);
    setIsFiltersMenuOpen(false);
  }, [setQueryState, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters({
      action_taken_by: AUDIT_LOG_CLEARED.action_taken_by,
      model_audit_log_state_id: AUDIT_LOG_CLEARED.model_audit_log_state_id,
      model_name: AUDIT_LOG_CLEARED.model_name,
    });
    setQueryState(AUDIT_LOG_CLEARED);
    setIsFiltersMenuOpen(false);
  }, [setQueryState]);

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryState({ page });
    },
    [setQueryState]
  );

  const resolveSelectedRows = useCallback(async () => {
    try {
      setIsUpdating(true);

      const promises = Object.keys(selectedRows)
        .filter((logId) => !!selectedRows[parseInt(logId)])
        .map((logId) =>
          ModelAuditLogService.updateState({
            model_audit_log_id: parseInt(logId),
            model_audit_log_state_id: ModelAuditLogState.Resolved,
          })
        );

      await Promise.all(promises);
      toast.success(toastMessages.resolveAuditLogs.success);
      setAllRowsSelected(false);
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: ['model-audit-logs'] });
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.resolveAuditLogs.error);
    } finally {
      setIsUpdating(false);
    }
  }, [selectedRows, queryClient, globalStore]);

  /* Effects */
  const handleSelectAll = useCallback(
    (selectAll: boolean) => {
      setAllRowsSelected(selectAll);
      if (selectAll) {
        setSelectedRows((prev) => {
          const next = { ...prev };
          modelAuditLogs?.forEach((log) => (next[log.id] = true));
          return next;
        });
      } else {
        setSelectedRows({});
      }
    },
    [modelAuditLogs]
  );

  useEffect(() => {
    setAllRowsSelected(false);
    setSelectedRows({});
  }, [queryState.page, queryState.action_taken_by, queryState.model_audit_log_state_id, queryState.model_name]);

  /* Sync page from API response */
  useEffect(() => {
    if (metadata && metadata.current_page !== queryState.page) {
      setQueryState({ page: metadata.current_page });
    }
  }, [metadata?.current_page]);

  /* Sync drafts when queryState changes */
  useSyncDraft(queryState, setDraftFilters, (s) => ({
    action_taken_by: s.action_taken_by ?? '',
    model_audit_log_state_id: s.model_audit_log_state_id ?? 0,
    model_name: s.model_name ?? '',
  }));

  /* Render helpers */
  const renderClientColumn = (log: ModelAuditLog) => {
    const isDelete = log.model_audit_log_event_type.name === ModelAuditLogEventType.Delete;
    const isCreate = log.model_audit_log_event_type.name === ModelAuditLogEventType.Create;
    const modelData = log.model ?? ({} as Model);
    const client =
      log.model_name === ModelName.User
        ? (modelData as User).clients?.[0]
        : log.model_name === ModelName.ClientFile
          ? (modelData as ClientFile)?.client
          : (modelData as Facility | Equipment)?.client;

    let clientIdFromData: number | undefined;

    if (isDelete && !log.model && log.old_data) {
      const oldData = log.old_data as Record<string, unknown>;
      if (oldData.clientId) {
        clientIdFromData = oldData.clientId as number;
      } else if (oldData.client_id) {
        clientIdFromData = oldData.client_id as number;
      }
    }

    if (isCreate && !log.model && log.new_data) {
      const newData = log.new_data as Record<string, unknown>;
      if (newData.clientId) {
        clientIdFromData = newData.clientId as number;
      } else if (newData.client_id) {
        clientIdFromData = newData.client_id as number;
      }
    }

    return (
      <Box overflow={{ horizontal: 'hidden' }}>
        {client &&
          (!isDelete ? (
            <Link to={`/clients/${client.id}`}>
              <Text fontFamily="Lato, sans-serif" color="accent-1">
                {client.name}
              </Text>
            </Link>
          ) : (
            <Text fontFamily="Lato, sans-serif" color="accent-1">
              {client.name}
            </Text>
          ))}
        {!client && clientIdFromData && (
          <Link to={`/clients/${clientIdFromData}`}>
            <Text fontFamily="Lato, sans-serif" color="accent-1">
              Client ID: {clientIdFromData}
            </Text>
          </Link>
        )}
        {!client && !clientIdFromData && '—'}
      </Box>
    );
  };

  const renderModelNameColumn = (log: ModelAuditLog) => {
    const isEquipmentUsage = log.model_name === ModelName.EquipmentUsage;
    const hasModel = !!log.model;
    const modelLabel = getModelLabel(log) || '';
    const isRecognizedModel = Object.values(ModelName).includes(log.model_name as ModelName);

    return (
      <DataTableItem
        icon={<EntityIcon entityName={log.model_name} size="18" color="accent-1" />}
        boxProps={{
          direction: 'row',
          align: 'center',
          gap: 'xsmall',
          overflow: { horizontal: 'hidden' },
        }}
      >
        {hasModel && !isEquipmentUsage ? (
          <Link to={getModelUrl(log.model, log.model_name as ModelName)}>
            <Text
              fontFamily="Lato, sans-serif"
              color="accent-1"
              style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
            >
              {modelLabel}
            </Text>
          </Link>
        ) : isEquipmentUsage ? (
          <Text fontFamily="Lato, sans-serif" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
            Equipment Usage
          </Text>
        ) : !isRecognizedModel ? (
          <Text fontFamily="Lato, sans-serif" color="dark-4" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
            {log.model_name}
          </Text>
        ) : (
          <Text
            fontFamily="Lato, sans-serif"
            color="dark-4"
            style={{ wordBreak: 'break-word', whiteSpace: 'normal', fontStyle: 'italic' }}
          >
            {modelLabel}
          </Text>
        )}
      </DataTableItem>
    );
  };

  const getChangedField = (oldData: Record<string, unknown>, newData: Record<string, unknown>) => {
    const changes: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];
    const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);

    allKeys.forEach((key) => {
      const oldValue = oldData?.[key];
      const newValue = newData?.[key];

      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({ field: key, oldValue, newValue });
      }
    });

    return changes;
  };

  const formatField = (field: string) => {
    return field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderExpandedRow = (log: ModelAuditLog) => {
    const changes = log.old_data && log.new_data ? getChangedField(log.old_data, log.new_data) : [];
    const hasChanges = changes.length > 0;
    const isDelete = log.model_audit_log_event_type.name === ModelAuditLogEventType.Delete;
    const isCreate = log.model_audit_log_event_type.name === ModelAuditLogEventType.Create;
    const dataToDisplay = isDelete ? log.old_data : isCreate ? log.new_data : null;
    const dataFields = dataToDisplay
      ? Object.keys(dataToDisplay).map((key) => ({
          field: key,
          value: dataToDisplay[key],
        }))
      : [];

    return (
      <Box
        pad={{ horizontal: 'large', vertical: 'medium' }}
        background="light-4"
        border={{ side: 'bottom', color: 'light-4' }}
      >
        <Box direction="row" gap="large">
          <Box flex="grow" gap="small">
            <Text weight="bold" size="small" color="dark-3">
              REQUEST DETAILS
            </Text>
            <Box gap="xsmall">
              <Box direction="row" gap="small">
                <Text size="small" weight="bold" fontFamily="Lato, sans-serif">
                  Method:
                </Text>
                <Text size="small" fontFamily="Lato, sans-serif">
                  {log.request_method}
                </Text>
              </Box>
              <Box direction="row" gap="small">
                <Text size="small" weight="bold" fontFamily="Lato, sans-serif">
                  URL:
                </Text>
                <Text size="small" fontFamily="Lato, sans-serif" wordBreak="break-all">
                  {log.request_url}
                </Text>
              </Box>
              <Box direction="row" gap="small">
                <Text size="small" weight="bold" fontFamily="Lato, sans-serif">
                  IP:
                </Text>
                <Text size="small" fontFamily="Lato, sans-serif">
                  {log.request_ip}
                </Text>
              </Box>
            </Box>
          </Box>

          {hasChanges && (
            <Box flex="grow" gap="small">
              <Text weight="bold" size="small" color="dark-3">
                CHANGES MADE
              </Text>
              <Box
                background="white"
                pad="small"
                round="xsmall"
                border={{ color: 'light-3' }}
                overflow="auto"
                style={{ maxHeight: '300px' }}
              >
                {changes.map((change, index) => (
                  <Box
                    key={index}
                    direction="row"
                    gap="small"
                    pad="small"
                    border={{ side: 'bottom', color: 'light-3' }}
                    style={{ borderBottom: index === changes.length - 1 ? 'none' : undefined }}
                    align="center"
                  >
                    <Box width="300px" flex="shrink">
                      <Text size="small" weight="bold" fontFamily="Lato, sans-serif">
                        {formatField(change.field)}
                      </Text>
                    </Box>
                    <Box flex="grow" direction="row" gap="small" align="center">
                      <Box flex basis="1/2">
                        <Text size="xsmall" weight="bold" fontFamily="Lato, sans-serif" color="dark-4">
                          Old Value:
                        </Text>
                        <Text
                          size="xsmall"
                          fontFamily="monospace"
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {formatValue(change.oldValue)}
                        </Text>
                      </Box>
                      <Box flex basis="1/2">
                        <Text size="xsmall" weight="bold" fontFamily="Lato, sans-serif" color="dark-4">
                          New Value:
                        </Text>
                        <Text
                          size="xsmall"
                          fontFamily="monospace"
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {formatValue(change.newValue)}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {!hasChanges && (isDelete || isCreate) && dataFields.length > 0 && (
            <Box flex="grow" gap="small">
              <Text weight="bold" size="small" color="dark-3">
                {isDelete ? 'DELETED DATA' : 'CREATED DATA'}
              </Text>
              <Box
                background="white"
                pad="small"
                round="xsmall"
                border={{ color: 'light-3' }}
                overflow="auto"
                style={{ maxHeight: '300px' }}
              >
                {dataFields.map((item, index) => (
                  <Box
                    key={index}
                    direction="row"
                    gap="small"
                    pad="small"
                    border={{ side: 'bottom', color: 'light-3' }}
                    style={{ borderBottom: index === dataFields.length - 1 ? 'none' : undefined }}
                    align="center"
                  >
                    <Box width="300px" flex="shrink">
                      <Text size="small" weight="bold" fontFamily="Lato, sans-serif">
                        {formatField(item.field)}
                      </Text>
                    </Box>
                    <Box flex="grow">
                      <Text
                        size="xsmall"
                        fontFamily="monospace"
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {formatValue(item.value)}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {log.resolved_by_user && (
            <Box flex="shrink" gap="small">
              <Text weight="bold" size="small" color="dark-3">
                RESOLVED BY
              </Text>
              <Box gap="xsmall">
                <Text size="small" fontFamily="Lato, sans-serif">
                  {log.resolved_by_user.name}
                </Text>
                <Text size="xsmall" fontFamily="Lato, sans-serif" color="dark-4">
                  {log.resolved_by_user.email}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box fill="horizontal">
      <Box direction="row" gap="medium">
        <Card {...boxProps} flex>
          {!hideHeader && (
            <CardHeader title={title || 'Change Log List'} icon={<AuditLogIcon />}>
              <Box direction="row" gap="medium">
                <AddButton
                  label="Resolve Selected Logs"
                  onClick={() => resolveSelectedRows()}
                  disabled={!hasSelectedRows}
                />
                <DropButton
                  plain
                  icon={<Filter color={isFiltersMenuHovered || isFiltersMenuOpen ? 'brand' : undefined} />}
                  open={isFiltersMenuOpen}
                  onOpen={() => setIsFiltersMenuOpen(true)}
                  onClose={() => setIsFiltersMenuOpen(false)}
                  onMouseOver={() => setIsFiltersMenuHovered(true)}
                  onMouseOut={() => setIsFiltersMenuHovered(false)}
                  tip={tooltips.filterLogs}
                  dropAlign={{ right: 'right', top: 'bottom' }}
                  dropContent={
                    <FiltersDropdown
                      isFiltering={isRefetching}
                      onSubmit={() => applyFilters()}
                      onClear={() => clearFilters()}
                      filters={[
                        {
                          label: 'Status',
                          value: draftFilters.model_audit_log_state_id,
                          setValue: (value) => setDraftFilters({ ...draftFilters, model_audit_log_state_id: value }),
                          options: statusFilterOptions,
                          disabled:
                            draftFilters.action_taken_by === AuditorType.System ||
                            draftFilters.action_taken_by === AuditorType.InternalUser,
                        },
                        {
                          label: 'Model Type',
                          value: draftFilters.model_name,
                          setValue: (value) => {
                            setDraftFilters({ ...draftFilters, model_name: value });
                          },
                          options: [
                            { label: 'All', value: '' },
                            { label: 'Client', value: ModelName.Client },
                            { label: 'Client File', value: ModelName.ClientFile },
                            { label: 'Facility', value: ModelName.Facility },
                            { label: 'Equipment', value: ModelName.Equipment },
                            { label: 'Equipment Usage', value: ModelName.EquipmentUsage },
                          ],
                        },
                        {
                          label: 'Action Taken By',
                          value: draftFilters.action_taken_by,
                          setValue: (value) =>
                            setDraftFilters({
                              ...draftFilters,
                              action_taken_by: value,
                              model_audit_log_state_id:
                                value !== AuditorType.ExternalUser
                                  ? 0
                                  : draftFilters.model_audit_log_state_id === 0
                                    ? ModelAuditLogState.Unresolved
                                    : draftFilters.model_audit_log_state_id,
                            }),
                          options: [
                            { label: 'All', value: '' },
                            { label: 'System', value: AuditorType.System },
                            { label: 'Internal User', value: AuditorType.InternalUser },
                            { label: 'External User', value: AuditorType.ExternalUser },
                          ],
                        },
                      ]}
                    />
                  }
                />
              </Box>
            </CardHeader>
          )}
          <CardBody pad="none" gap="none">
            <Box elevation="small" overflow={{ horizontal: 'auto' }}>
              {(_isLoading || isUpdating) && <LoadingSpinner />}
              {!_isLoading && !isUpdating && (
                <Box style={{ minWidth: '900px' }}>
                  <Box
                    direction="row"
                    pad={{ horizontal: '1rem', vertical: 'xsmall' }}
                    background="white"
                    border={{ side: 'bottom', color: 'light-4' }}
                    gap="12px"
                  >
                    <Box width="7%">
                      <DataTableHeader>
                        <CheckBox
                          name="all_rows_selected"
                          checked={allRowsSelected}
                          onChange={() => handleSelectAll(!allRowsSelected)}
                        />
                      </DataTableHeader>
                    </Box>
                    <Box width="7%">
                      <DataTableHeader title="EVENT" />
                    </Box>
                    <Box width="19%">
                      <DataTableHeader title="CLIENT" />
                    </Box>
                    <Box width="25%">
                      <DataTableHeader title="MODEL NAME" />
                    </Box>
                    <Box width="20%">
                      <DataTableHeader title="USER" />
                    </Box>
                    <Box width="12%">
                      <DataTableHeader title="DATE" />
                    </Box>
                    <Box width="10%" direction="row" align="center" gap="xsmall">
                      <DataTableHeader title="STATUS" />
                      <Tip content={tooltips.logStatus}>
                        <CircleInformation size="14px" />
                      </Tip>
                    </Box>
                  </Box>

                  {modelAuditLogs?.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <Box
                        direction="row"
                        pad={{ horizontal: '1rem' }}
                        background={index % 2 === 0 ? 'light-6' : 'white'}
                        border={{ side: 'bottom', color: 'light-2', size: 'small' }}
                        gap="12px"
                      >
                        <Box
                          width="7%"
                          direction="row"
                          gap="medium"
                          pad={{ vertical: '1.25rem' }}
                          style={{ alignItems: 'center' }}
                        >
                          <CheckBox
                            name={log.id?.toString()}
                            checked={!!selectedRows[log.id]}
                            onChange={() => {
                              setSelectedRows({ ...selectedRows, [log.id]: !selectedRows[log.id] });
                            }}
                          />
                          <Link to={`/change-logs/${log.id}`} color="accent-1" style={{ display: 'flex' }}>
                            <Tip content={tooltips.viewLogDetails}>
                              <SettingsOption size="18px" color="accent-1" />
                            </Tip>
                          </Link>
                        </Box>

                        <Box width="7%" pad={{ vertical: '1.25rem' }} justify="center">
                          <Box
                            onClick={() => {
                              setExpandedRows({ ...expandedRows, [log.id]: !expandedRows[log.id] });
                            }}
                            style={{ cursor: 'pointer', color: 'accent-1' }}
                          >
                            <DataTableItem
                              value={log.model_audit_log_event_type.name}
                              color="accent-1"
                              icon={
                                <AuditLogEventTypeIcon
                                  auditLogEventTypeName={log.model_audit_log_event_type.name}
                                  size="14px"
                                />
                              }
                            />
                          </Box>
                        </Box>

                        <Box width="19%" pad={{ vertical: '1.25rem' }} justify="center">
                          {renderClientColumn(log)}
                        </Box>

                        <Box
                          width="25%"
                          pad={{ vertical: '1.25rem' }}
                          justify="center"
                          overflow={{ horizontal: 'hidden' }}
                        >
                          {renderModelNameColumn(log)}
                        </Box>

                        <Box width="20%" pad={{ vertical: '1.25rem' }} justify="center">
                          <DataTableItem>
                            {log.user ? (
                              <Box>
                                <Link to={`/users/${log.user?.id}`} textDecoration="none">
                                  <Text
                                    color="accent-1"
                                    size="medium"
                                    weight={700}
                                    fontFamily="Lato, sans-serif"
                                    lineHeight="1rem"
                                  >
                                    {log.user?.name}
                                  </Text>
                                </Link>
                                <Text
                                  size="small"
                                  fontFamily="Lato, sans-serif"
                                  lineHeight="1.25rem"
                                  wordBreak="break-all"
                                >
                                  {log.user?.email}
                                </Text>
                                <Text size="small" fontFamily="Lato, sans-serif" lineHeight="1rem">
                                  {log.user?.phone}
                                </Text>
                              </Box>
                            ) : (
                              <Text>—</Text>
                            )}
                          </DataTableItem>
                        </Box>

                        <Box width="12%" pad={{ vertical: '1.25rem' }} justify="center">
                          <DataTableItem value={dayjs(log.created_at).format('MM/DD/YYYY')} />
                        </Box>
                        <Box width="10%" pad={{ vertical: '1.25rem' }} justify="center">
                          <DataTableItem
                            value={
                              log.model_audit_log_state_id === null
                                ? '—'
                                : log.model_audit_log_state_id === ModelAuditLogState.Resolved
                                  ? 'Resolved'
                                  : 'Unresolved'
                            }
                            icon={
                              log.model_audit_log_state_id === null ? undefined : log.model_audit_log_state_id ===
                                ModelAuditLogState.Resolved ? (
                                <Checkmark size="16px" />
                              ) : (
                                <CircleAlert size="16px" />
                              )
                            }
                          />
                        </Box>
                      </Box>

                      <Collapsible open={expandedRows[log.id]}>{renderExpandedRow(log)}</Collapsible>
                    </React.Fragment>
                  ))}
                </Box>
              )}
              {!hasModelAuditLogs && !_isLoading && (
                <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
                  <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                    No audit logs found.
                  </Text>
                </Box>
              )}
            </Box>
            {hasModelAuditLogs && !!metadata && !!queryState.page && (
              <Box pad="1.5rem" flex="grow">
                <Pagination
                  alignSelf="center"
                  size="small"
                  page={queryState.page}
                  step={metadata.per_page}
                  numberItems={metadata.total}
                  onChange={({ page }) => handlePageChange(page)}
                />
              </Box>
            )}
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export type ModelAuditLogListProps = BoxProps & {
  title?: string;
  showFilters?: boolean;
  hideHeader?: boolean;
  surfaceId?: string;
};
